import { Component, HostListener, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ConsultarCotizacionService } from '../../../../../../services/cotizador-cotizacion/consultar-cotizacion.service';
import { CampaniaDetalleItem } from '../../../../../../models/cotizador-cotizacion/consultar-campania-detalle/response/consultar-campania-detalle-response.model';

export interface ModalDetalleCotizacionData {
  idCotizacion?: number | null;
  /** PK COTIZACION_CAMPANIA: si existe, se cargan % por mes desde el backend. */
  idCotizacionCampania?: number | null;
  nombreBeneficiario: string;
  lineaNegocio: string;
  programa: string;
  tarifaMensual: number;
  descripcionTarifa: string;
  campaniaCodigo: string;
  campaniaNombre: string;
  campaniaInicioVigencia: string;
  campaniaFinVigencia: string;
  proporcionalidadValor: number;
  descuentoProntoPagoPorcentaje: number;
  cuotaInscripcionValor: number;
  tarifaComoAsociadoValor: number;
  /** Cantidad de meses de pronto pago (N): se muestran meses 1..N. */
  mesesProntoPago: number;
  /** Colectivo / sin liquidación pronto pago: no se muestra tabla ni totales (solo parámetros). */
  ocultarLiquidacionProntoPago?: boolean;
  /** Ventas nuevas plan colectivo (C/GA/AG): no mostrar proporcionalidad en parámetros. */
  ocultarProporcionalidad?: boolean;
}

interface DetalleMes {
  mes: number;
  descuentoCampaniaPorcentaje: number;
  valorMes: number;
}

@Component({
  selector: 'app-modal-detalle-cotizacion',
  templateUrl: './modal-detalle-cotizacion.component.html',
  styleUrls: ['./modal-detalle-cotizacion.component.css']
})
export class ModalDetalleCotizacionComponent implements OnInit {
  detalleMeses: DetalleMes[] = [];
  cargandoDetalleCampania = false;
  errorDetalleCampania: string | null = null;

  readonly displayedColumns: string[] = ['mes', 'descuento', 'valorMes'];

  constructor(
    private dialogRef: MatDialogRef<ModalDetalleCotizacionComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ModalDetalleCotizacionData,
    private consultarCotizacionService: ConsultarCotizacionService
  ) {}

  ngOnInit(): void {
    this.cargarDetalleMeses();
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.cerrar();
  }

  cerrar(): void {
    this.dialogRef.close();
  }

  get totalSinDescuentoProntoPago(): number {
    return this.detalleMeses.reduce((acc, row) => acc + row.valorMes, 0);
  }

  get valorDescuentoProntoPago(): number {
    return this.totalSinDescuentoProntoPago * (this.data.descuentoProntoPagoPorcentaje / 100);
  }

  get totalConDescuento(): number {
    return this.totalSinDescuentoProntoPago - this.valorDescuentoProntoPago;
  }

  formatCurrency(value: number): string {
    return new Intl.NumberFormat('es-CO').format(Math.round(value || 0));
  }

  private cargarDetalleMeses(): void {
    const idCc = this.data.idCotizacionCampania;
    if (idCc == null || !Number.isFinite(Number(idCc)) || Number(idCc) <= 0) {
      this.construirDetalleMesesSinCampaniaBackend();
      return;
    }

    this.cargandoDetalleCampania = true;
    this.errorDetalleCampania = null;

    this.consultarCotizacionService.consultarCampaniaDetalle(Number(idCc)).subscribe({
      next: (resp) => {
        this.cargandoDetalleCampania = false;
        if (resp?.status && resp.data?.detalles) {
          this.construirDetalleMesesDesdeDetalles(resp.data.detalles);
        } else {
          this.errorDetalleCampania = resp?.message || 'No se pudo cargar el detalle de campaña.';
          this.construirDetalleMesesSinCampaniaBackend();
        }
      },
      error: () => {
        this.cargandoDetalleCampania = false;
        this.errorDetalleCampania = 'Error al consultar el detalle de campaña.';
        this.construirDetalleMesesSinCampaniaBackend();
      }
    });
  }

  /**
   * N meses de pronto pago = filas mes 1 .. N; % por `numeroCuota` del maestro.
   */
  private getMesesParaGrilla(): number {
    return Math.max(this.data.mesesProntoPago || 1, 1);
  }

  private construirDetalleMesesDesdeDetalles(detalles: CampaniaDetalleItem[]): void {
    const meses = this.getMesesParaGrilla();
    const mapaPct = new Map<number, number>();
    detalles.forEach((d) => {
      const n = d.numeroCuota;
      const p = Number(d.porcentajeDescuento ?? 0);
      if (n != null && Number.isFinite(n) && n > 0 && Number.isFinite(p)) {
        mapaPct.set(Math.floor(n), p);
      }
    });

    const tarifa = Number(this.data.tarifaMensual || 0);
    this.detalleMeses = Array.from({ length: meses }, (_, idx) => {
      const mes = idx + 1;
      const descuentoCampaniaPorcentaje = mapaPct.get(mes) ?? 0;
      const valorMes = tarifa * (1 - descuentoCampaniaPorcentaje / 100);
      return { mes, descuentoCampaniaPorcentaje, valorMes };
    });
  }

  /** Sin campaña o sin id: todos los meses con 0% descuento campaña. */
  private construirDetalleMesesSinCampaniaBackend(): void {
    const meses = this.getMesesParaGrilla();
    const tarifa = Number(this.data.tarifaMensual || 0);
    this.detalleMeses = Array.from({ length: meses }, (_, idx) => {
      const mes = idx + 1;
      return { mes, descuentoCampaniaPorcentaje: 0, valorMes: tarifa };
    });
  }
}
